import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {AsyncHandlerOf, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  addLink,
  CardAttributeUpdateAction,
  deleteLink,
  updateLink,
} from '../../../helpers/models/cardAttributeUpdateHelpers';
import {CardModel, Link} from '../../../models/api/cardModel';
import {LinkifyFormatter} from '../../common/formatters/markdown/linkifyFormatter';
import {InlineEditingTextBox} from '../../common/inlineEditing/inlineEditingTextBox';
import {LinkTrap} from '../../common/linkTrap/linkTrap';
import {useContactDetailsContext} from '../contactDetailsContext';
import {ContactInformationAttribute} from './contactInformationAttribute';

/*
 * Props.
 */

interface LinksSectionProps {
  card: CardModel;
  updateCardAttributeAsync: AsyncHandlerOf<CardAttributeUpdateAction>;
  updateUnsavedCard: HandlerOf<CardAttributeUpdateAction>;
}

/*
 * Style.
 */

const StyledLinkDiv = styled.div`
  margin: 8px 0 7px 0;
`;

/*
 * Component.
 */

export const LinksSection: FC<LinksSectionProps> = (props) => {
  const {card, updateCardAttributeAsync, updateUnsavedCard} = props;
  const contactDetailsContext = useContactDetailsContext();

  return (
    <>
      {card.links.map((link, index) =>
        renderLink(
          link,
          index,
          contactDetailsContext.isReadOnly,
          updateCardAttributeAsync,
          updateUnsavedCard,
        ),
      )}
    </>
  );
};

function renderLink(
  link: Link,
  index: number,
  isReadOnly: boolean,
  updateCardAttributeAsync: AsyncHandlerOf<CardAttributeUpdateAction>,
  updateUnsavedCard: HandlerOf<CardAttributeUpdateAction>,
) {
  const isAddingNewLink = link.url === '';
  return (
    <ContactInformationAttribute
      key={link.url + index}
      interactionId={`contact-details-link-${index}`}
      title={<Trans id="contact-details-contact-information-link">Link</Trans>}
      renderValue={({isEditing, editedValue, onEdit, onEditSave, onEditCancel}) => {
        if (isEditing) {
          return (
            <InlineEditingTextBox
              value={editedValue}
              onEdit={onEdit}
              onEditSave={onEditSave}
              onEditCancel={onEditCancel}
            />
          );
        }

        if (isAddingNewLink) {
          return <StyledLinkDiv>-</StyledLinkDiv>;
        }

        if (isReadOnly) {
          return <StyledLinkDiv>{link.url}</StyledLinkDiv>;
        }

        return (
          <StyledLinkDiv>
            <LinkTrap>
              <LinkifyFormatter text={link.url} />
            </LinkTrap>
          </StyledLinkDiv>
        );
      }}
      currentAttributeValue={link.url}
      onSave={(newValue: string) => {
        const action = computeOnSaveAction(isAddingNewLink, link.url, index, newValue);
        return updateCardAttributeAsync(action);
      }}
      onDelete={() => {
        const action = deleteLink(link.url, index);

        // No need to hit the backend if the user deletes the link before it's saved
        if (isAddingNewLink) {
          return updateUnsavedCard(action);
        }

        return updateCardAttributeAsync(action);
      }}
      shouldStartInEditMode={isAddingNewLink}
    />
  );
}

/*
 * Helpers.
 */

function computeOnSaveAction(isAddingNewLink: boolean, url: string, index: number, newValue: string) {
  if (isAddingNewLink) {
    return addLink(newValue, index);
  }

  // If the user empties the field, treat it as a delete
  if (!newValue) {
    return deleteLink(url, index);
  }

  return updateLink(url, newValue, index);
}
